import {DiscordContentFrameCommunicationHandler} from './DiscordContentFrameCommunicationHandler.ts'
import {type SDKRequest, DiscordCommand} from '../../../../../shared/DiscordActivityTypes.ts'
import {uiManager} from '../../EntryHomepage.ts'
import {uiGameManager} from '../../../../shared/scripts/EntryShared.ts'
import {Scene} from '../../ui_html/UISceneTypes.ts'
import {type UIDLoadingScreen} from './components/UIDLoadingScreen.ts'
import {Events} from '@discord/embedded-app-sdk'
import {UIAjaxWrapper} from '../../../../shared/scripts/network/UIAjaxWrapper.ts'
import {UIAPIEndpoints} from '../../../../shared/scripts/network/UIAPIEndpoints.ts'
import {ArrayUtils} from '@colonist/utils'
import {richPresenceDetails, richPresenceStates} from '../utils/RichPresenceText.ts'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState.ts'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables.ts'
import {SDKRequestHandler} from './sdk_requests/SDKRequestHandler.ts'
import {StorageWrapper} from '../../../../shared/scripts/StorageWrapper.ts'
import {UINotificationSendHelper} from '../../../../shared/scripts/notifications/UINotificationSendHelper.ts'
import {GameStateEnums} from '../../../../../shared/GameEnums.ts'
import {type DiscordSnowflake} from '@colonist/types'
import {UIReferralProgramController} from '../../referral/UIReferralProgramController.ts'

export class DiscordContentFrameController {
    private readonly contentFrameCommunicationHandler: DiscordContentFrameCommunicationHandler
    loadingScreen: UIDLoadingScreen
    protected sdkRequestHandler: SDKRequestHandler
    private referralController: UIReferralProgramController

    constructor() {
        this.contentFrameCommunicationHandler = new DiscordContentFrameCommunicationHandler()
        this.sdkRequestHandler = new SDKRequestHandler(this.contentFrameCommunicationHandler)
    }

    get voiceChannelId() {
        return this.contentFrameCommunicationHandler.voiceChannelId
    }

    get serverProfile() {
        return this.contentFrameCommunicationHandler.serverProfile
    }

    get inDiscordServer() {
        return this.contentFrameCommunicationHandler.inDiscordServer
    }

    get canInviteFriends() {
        return this.contentFrameCommunicationHandler.userPermissions.canInviteFriends
    }

    get referrerId() {
        if(this.contentFrameCommunicationHandler.referrerId == 'undefined') return undefined
        return this.contentFrameCommunicationHandler.referrerId
    }

    get referralOrReactivationType() {
        if(this.contentFrameCommunicationHandler.referralTypeId == 'undefined') return undefined
        return this.contentFrameCommunicationHandler.referralTypeId
    }

    get isInitialLoad() {
        return this.contentFrameCommunicationHandler.isInitialLoad
    }

    async init(loadingScreen: UIDLoadingScreen) {
        this.loadingScreen = loadingScreen

        await this.contentFrameCommunicationHandler.waitToBeReady()

        await this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.SetActivity,
            data: {
                activity: {
                    details: ArrayUtils.randomElementFromArray(richPresenceDetails),
                    type: 0,
                    state: ArrayUtils.randomElementFromArray(richPresenceStates),
                },
            },
        })

        await this.contentFrameCommunicationHandler.sendMessageToMount({
            command: DiscordCommand.SetOrientation,
            data: {
                orientation: UIUtilsEjsVariables.discordActivityMobileOrientation,
            },
        })

        let firstRun = true
        const toggleFloatingWindowMode = Events.ACTIVITY_LAYOUT_MODE_UPDATE
        // eslint-disable-next-line @typescript-eslint/naming-convention
        void this.contentFrameCommunicationHandler.subscribeToDiscordEvent(toggleFloatingWindowMode, (eventData: {layout_mode: boolean}) => {
            if(firstRun) { // Mau: Discord Automatically sends this event on startup. This prevents the loading screen to hide before the game is loaded.
                firstRun = false
                return
            }
            DiscordContentFrameController.toggleFPSIfPipMode(eventData.layout_mode)
            if(eventData.layout_mode) {
                this.loadingScreen.hideSpinner()
                this.loadingScreen.showBackground()
                uiGameManager?.eventController.sendEvent(false, GameStateEnums.UIGameEventEnums.GameVisibilityUpdated)
                return
            }
            this.loadingScreen.hideBackground()
            this.loadingScreen.showSpinner()
            uiGameManager?.eventController.sendEvent(true, GameStateEnums.UIGameEventEnums.GameVisibilityUpdated)
        })

        const purchaseReceived = Events.ENTITLEMENT_CREATE
        await this.contentFrameCommunicationHandler.subscribeToDiscordEvent(purchaseReceived, async(data) => {
            await this.handlePendingPurchases()
        })

        const referrerId = this.referrerId as DiscordSnowflake | string
        const referralOrReactivationType = this.referralOrReactivationType as unknown

        this.referralController = new UIReferralProgramController(referrerId, referralOrReactivationType)

        await this.referralController.initReferralTracking()
    }

    static toggleFPSIfPipMode(pipMode: boolean) {
        if(UIUtilsEjsVariables.isProduction) return
        if(uiManager == undefined) return
        if(uiManager.sceneController.scene != Scene.Game) return
        if(uiGameManager.canvasController?.fps?.div == undefined) return
        uiGameManager.canvasController.fps.div.style.display = pipMode ? 'none' : 'block'
    }

    async handlePendingPurchases() {
        await ClientUserState.waitUntilFresh

        const giftMetadataId = StorageWrapper.getGiftMetadataId() ?? undefined
        const response = await UIAjaxWrapper.postRequestWhenReady(UIAPIEndpoints.apiHandleDiscordPurchase(), undefined, {accessToken: this.contentFrameCommunicationHandler.userDiscordAccessToken, giftMetadataId: giftMetadataId})

        if(response == undefined) return

        const purchaseSuccess = response.json as boolean
        if(!purchaseSuccess) return

        StorageWrapper.clearGiftMetadataId()
        await ClientUserState.refresh()

        UINotificationSendHelper.getNotifications()
        if(uiManager.sceneController.scene == Scene.DiscordGifting) uiManager.sceneController.loadScene(Scene.DiscordStore)
    }

    sendSDKRequest(sdkRequest: SDKRequest, data?: any) {
        this.sdkRequestHandler.processSDKRequest(sdkRequest, data)
    }
}
